import { useState, useMemo } from 'react'
import { format, parseISO } from 'date-fns'
import {
  ChevronUp,
  ChevronDown,
  Trash2,
  Plus,
  CalendarDays,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import {
  useReorderMilestones,
  useDeleteMilestone,
} from '@/hooks/useCalendarMilestones'
import { resolveAllDates } from '@/utils/calendarDateResolver'
import { cn } from '@/utils/cn'
import type { CalendarMilestoneRead } from '@/types'

interface MilestoneOverviewListProps {
  milestones: CalendarMilestoneRead[]
  allMilestones: CalendarMilestoneRead[]
  selectedId: string | null
  onSelect: (id: string) => void
  calendarId: string
  eventId: string
  onAddNew: () => void
}

export function MilestoneOverviewList({
  milestones,
  allMilestones,
  selectedId,
  onSelect,
  calendarId,
  eventId,
  onAddNew,
}: MilestoneOverviewListProps) {
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const reorderMilestones = useReorderMilestones(calendarId, eventId)
  const deleteMilestone = useDeleteMilestone(calendarId, eventId)

  const sortedMilestones = useMemo(
    () => [...milestones].sort((a, b) => a.display_order - b.display_order),
    [milestones]
  )

  const resolvedDates = useMemo(
    () => resolveAllDates(allMilestones),
    [allMilestones]
  )

  const handleReorder = (index: number, direction: 'up' | 'down') => {
    const newOrder = [...sortedMilestones]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    if (swapIndex < 0 || swapIndex >= newOrder.length) return
    ;[newOrder[index], newOrder[swapIndex]] = [
      newOrder[swapIndex],
      newOrder[index],
    ]
    reorderMilestones.mutate({ ordered_ids: newOrder.map(m => m.id) })
  }

  const handleDelete = () => {
    if (!deleteId) return
    deleteMilestone.mutate(deleteId, {
      onSuccess: () => setDeleteId(null),
    })
  }

  const formatResolvedDate = (milestoneId: string): string => {
    const dateStr = resolvedDates.get(milestoneId)
    if (!dateStr) return 'Unresolved'
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy')
    } catch {
      return 'Invalid date'
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto space-y-1 pr-1">
        {sortedMilestones.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <CalendarDays className="mb-2 h-6 w-6" />
            <p className="text-sm">No milestones yet.</p>
          </div>
        ) : (
          sortedMilestones.map((ms, index) => {
            const isSelected = selectedId === ms.id
            const resolvedDate = formatResolvedDate(ms.id)

            return (
              <div
                key={ms.id}
                className={cn(
                  'group flex items-center gap-2.5 rounded-md p-2 transition-colors cursor-pointer',
                  isSelected
                    ? 'bg-primary/5 ring-1 ring-primary/20'
                    : 'hover:bg-muted/40'
                )}
                onClick={() => onSelect(ms.id)}
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold">
                  {ms.display_order}
                </span>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium leading-snug">
                    {ms.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {resolvedDate}
                  </p>
                </div>

                <div
                  className={cn(
                    'flex shrink-0 items-center gap-0.5 transition-opacity',
                    isSelected
                      ? 'opacity-100'
                      : 'opacity-0 group-hover:opacity-100'
                  )}
                  onClick={e => e.stopPropagation()}
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 cursor-pointer"
                    disabled={index === 0 || reorderMilestones.isPending}
                    onClick={() => handleReorder(index, 'up')}
                  >
                    <ChevronUp className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 cursor-pointer"
                    disabled={
                      index === sortedMilestones.length - 1 ||
                      reorderMilestones.isPending
                    }
                    onClick={() => handleReorder(index, 'down')}
                  >
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 cursor-pointer text-destructive hover:text-destructive"
                    onClick={() => setDeleteId(ms.id)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            )
          })
        )}
      </div>

      <div className="shrink-0 mt-3 pt-3 border-t">
        <Button
          variant="outline"
          size="sm"
          className="w-full cursor-pointer gap-1.5"
          onClick={onAddNew}
        >
          <Plus className="h-4 w-4" />
          Add Milestone
        </Button>
      </div>

      <ConfirmationDialog
        open={!!deleteId}
        onOpenChange={open => {
          if (!open) setDeleteId(null)
        }}
        title="Delete Milestone"
        description="Are you sure you want to delete this milestone? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={deleteMilestone.isPending}
      />
    </div>
  )
}
